import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useUser } from "@/context/UserContext";
import { supabase } from "@/lib/supabase";
import type { ClockInRecord } from "@/types/clockIn";

function formatCurrency(num: number): string {
  return `NT$ ${num.toLocaleString("zh-TW")}`;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("zh-TW", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

function getWeekNumber(): number {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 0);
  const diff = now.getTime() - startOfYear.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 7));
}

function calcDailyRevenue(date: Date): number {
  // 日期決定模擬營收；同一天固定，以 180 萬為基準上下浮動 10%～20%。
  const day = Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000);
  let seed = Math.imul(day ^ 0x9e3779b9, 0x45d9f3b);
  seed = Math.imul(seed ^ (seed >>> 16), 0x45d9f3b);
  seed = (seed ^ (seed >>> 16)) >>> 0;
  const variation = 0.10 + ((seed >>> 1) % 1001) / 10000;
  const direction = (seed & 1) === 0 ? -1 : 1;
  return Math.round(1800000 * (1 + direction * variation));
}

function calcTodayRevenue(): number {
  return calcDailyRevenue(new Date());
}

function calcMonthRevenue(): number {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  let total = 0;
  for (let d = 1; d <= today.getDate(); d++) {
    const date = new Date(year, month, d);
    total += calcDailyRevenue(date);
  }
  return total;
}

function calcTodayAttendance(): { present: number; total: number; rate: string } {
  const now = new Date();
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24)
  );
  const seed = now.getFullYear() * 1000 + (now.getMonth() + 1) * 50 + now.getDate() * 3;
  // 實到人數 20~24，依日期變化
  const present = 20 + ((seed + dayOfYear * 7 + 13) % 5);
  const total = 26;
  const rate = Math.round((present / total) * 1000) / 10;
  return { present, total, rate: `${rate}%` };
}

function calcRevenueChange(): number {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  return Math.round(((calcDailyRevenue(today) / calcDailyRevenue(yesterday)) - 1) * 1000) / 10;
}

function getYearToDateRevenueTrend(): { date: string; revenue: number }[] {
  const today = new Date();
  const year = today.getFullYear();
  const startDate = new Date(year, 0, 1);
  const dayCount =
    Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  const data: { date: string; revenue: number }[] = [];


  for (let i = 0; i < dayCount; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    data.push({ date: `${mm}-${dd}`, revenue: calcDailyRevenue(d) });
  }

  return data;
}

function getXTickInterval(): number {
  const dayCount =
    Math.floor(
      (new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) /
        (1000 * 60 * 60 * 24)
    ) + 1;
  return Math.max(1, Math.floor(dayCount / 12));
}

function getDateSeed(): number {
  const now = new Date();
  return now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
}

function seededShuffle<T>(array: T[], seed: number): T[] {
  const arr = [...array];
  let s = seed;
  for (let i = arr.length - 1; i > 0; i--) {
    s = (s * 16807 + 0) % 2147483647;
    const j = s % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function seededRandomTimeLabel(index: number, seed: number): string {
  const now = new Date();
  // 用 index 和 seed 組合一個偽隨機值
  const r = ((seed * 37 + index * 13) % 100) / 100;
  if (r < 0.15) {
    const s = ((seed * 11 + index * 7) % 59) + 1;
    return `${s} 秒前`;
  }
  if (r < 0.4) {
    const m = ((seed * 23 + index * 5) % 29) + 1;
    return `${m} 分前`;
  }
  if (r < 0.65) {
    const h = ((seed * 17 + index * 3) % 5) + 1;
    return `${h} 小時前`;
  }
  if (r < 0.85) {
    const hoursAgo = ((seed * 31 + index * 2) % 12) + 1;
    const d = new Date(now.getTime() - hoursAgo * 3600000);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  return "昨天";
}

// 100 條完整通知資料
const ALL_NOTIFICATIONS_RAW = [
  { id: "n1", type: "shipment", title: "ORD-1001 客戶 五糧液 已到達台北市，金額 NT$156,000" },
  { id: "n2", type: "account", title: "ORD-1002 Chateau Margaux 備貨中，金額 NT$180,777" },
  { id: "n3", type: "cs", title: "CS-1003 ORD-1002 客戶要求修改配送地址" },
  { id: "n4", type: "shipment", title: "ORD-1004 Don Perignon 已完成付款，金額 NT$198,000" },
  { id: "n5", type: "cs", title: "CS-1005 ORD-1004 等待主管確認" },
  { id: "n6", type: "shipment", title: "ORD-1006 Lafite Rothschild 已建立，金額 NT$320,000" },
  { id: "n7", type: "shipment", title: "ORD-1007 Opus One 已出貨，金額 NT$98,000" },
  { id: "n8", type: "cs", title: "CS-1008 ORD-1007 客戶要求提前配送" },
  { id: "n9", type: "shipment", title: "ORD-1009 Penfolds Grange 已完成驗貨，金額 NT$228,000" },
  { id: "n10", type: "shipment", title: "ORD-1010 Petrus 已付款，金額 NT$958,000" },
  { id: "n11", type: "cs", title: "CS-1011 ORD-1010 等待報關完成" },
  { id: "n12", type: "shipment", title: "ORD-1012 Vega Sicilia Unico 備貨完成，金額 NT$150,000" },
  { id: "n13", type: "shipment", title: "ORD-1013 Sassicaia 已配送完成，金額 NT$88,000" },
  { id: "n14", type: "cs", title: "CS-1014 ORD-1013 客戶確認收貨" },
  { id: "n15", type: "shipment", title: "ORD-1015 Ornellaia 已建立，金額 NT$136,000" },
  { id: "n16", type: "shipment", title: "ORD-1016 Masseto 已付款，金額 NT$196,000" },
  { id: "n17", type: "cs", title: "CS-1017 ORD-1016 等待物流安排" },
  { id: "n18", type: "shipment", title: "ORD-1018 Cheval Blanc 已建立，金額 NT$428,000" },
  { id: "n19", type: "shipment", title: "ORD-1019 Chateau Latour 已出貨，金額 NT$266,000" },
  { id: "n20", type: "cs", title: "CS-1020 ORD-1019 客戶取消部分商品" },
  { id: "n21", type: "shipment", title: "ORD-1021 Chateau Haut-Brion 已完成付款，金額 NT$246,000" },
  { id: "n22", type: "shipment", title: "ORD-1022 Chateau Palmer 已建立，金額 NT$168,000" },
  { id: "n23", type: "cs", title: "CS-1023 ORD-1022 等待主管核准" },
  { id: "n24", type: "shipment", title: "ORD-1024 Chateau Canon 已付款，金額 NT$118,000" },
  { id: "n25", type: "shipment", title: "ORD-1025 Chateau Pavie 已配送完成，金額 NT$188,000" },
  { id: "n26", type: "cs", title: "CS-1026 ORD-1025 客戶要求修改發票" },
  { id: "n27", type: "shipment", title: "ORD-1027 Chateau Angelus 已建立，金額 NT$178,000" },
  { id: "n28", type: "shipment", title: "ORD-1028 Chateau Ausone 已付款，金額 NT$498,000" },
  { id: "n29", type: "cs", title: "CS-1029 ORD-1028 等待付款確認" },
  { id: "n30", type: "shipment", title: "ORD-1030 Chateau Figeac 已完成配送，金額 NT$142,000" },
  { id: "n31", type: "shipment", title: "ORD-1031 Dominus Estate 已建立，金額 NT$126,000" },
  { id: "n32", type: "cs", title: "CS-1032 ORD-1031 客戶要求更改配送日期" },
  { id: "n33", type: "shipment", title: "ORD-1033 Tignanello 已付款，金額 NT$76,000" },
  { id: "n34", type: "shipment", title: "ORD-1034 Clos Apalta 已出貨，金額 NT$82,000" },
  { id: "n35", type: "cs", title: "CS-1035 ORD-1034 等待物流取件" },
  { id: "n36", type: "shipment", title: "ORD-1036 Chateau Mouton Rothschild 已建立，金額 NT$298,000" },
  { id: "n37", type: "shipment", title: "ORD-1037 Chateau Lynch Bages 已付款，金額 NT$96,000" },
  { id: "n38", type: "cs", title: "CS-1038 ORD-1037 報關完成" },
  { id: "n39", type: "shipment", title: "ORD-1039 Chateau Pichon Baron 已建立，金額 NT$128,000" },
  { id: "n40", type: "shipment", title: "ORD-1040 Chateau Pontet Canet 已配送完成，金額 NT$116,000" },
  { id: "n41", type: "cs", title: "CS-1041 ORD-1040 客戶要求加急配送" },
  { id: "n42", type: "shipment", title: "ORD-1042 Chateau Montrose 已付款，金額 NT$156,000" },
  { id: "n43", type: "shipment", title: "ORD-1043 Chateau Calon Segur 已建立，金額 NT$105,000" },
  { id: "n44", type: "cs", title: "CS-1044 ORD-1043 等待客戶回覆" },
  { id: "n45", type: "shipment", title: "ORD-1045 Chateau Smith Haut Lafitte 已出貨，金額 NT$122,000" },
  { id: "n46", type: "shipment", title: "ORD-1046 Chateau Giscours 已付款，金額 NT$68,000" },
  { id: "n47", type: "cs", title: "CS-1047 ORD-1046 客戶要求修改聯絡電話" },
  { id: "n48", type: "shipment", title: "ORD-1048 Chateau Beychevelle 已建立，金額 NT$98,000" },
  { id: "n49", type: "shipment", title: "ORD-1049 Chateau Talbot 已完成付款，金額 NT$88,000" },
  { id: "n50", type: "cs", title: "CS-1050 ORD-1049 等待收件確認" },
  { id: "n51", type: "shipment", title: "ORD-1051 Chateau Clerc Milon 已配送完成，金額 NT$92,000" },
  { id: "n52", type: "shipment", title: "ORD-1052 Chateau Grand Puy Lacoste 已建立，金額 NT$102,000" },
  { id: "n53", type: "cs", title: "CS-1053 ORD-1052 客戶要求重新開立發票" },
  { id: "n54", type: "shipment", title: "ORD-1054 Chateau Duhart Milon 已付款，金額 NT$86,000" },
  { id: "n55", type: "shipment", title: "ORD-1055 Chateau Brane Cantenac 已出貨，金額 NT$84,000" },
  { id: "n56", type: "cs", title: "CS-1056 ORD-1055 等待配送完成" },
  { id: "n57", type: "shipment", title: "ORD-1057 Chateau Rauzan Segla 已建立，金額 NT$108,000" },
  { id: "n58", type: "shipment", title: "ORD-1058 Chateau Lagrange 已付款，金額 NT$72,000" },
  { id: "n59", type: "cs", title: "CS-1059 ORD-1058 客戶要求取消訂單" },
  { id: "n60", type: "shipment", title: "ORD-1060 Chateau Gloria 已完成配送，金額 NT$65,000" },
  { id: "n61", type: "shipment", title: "ORD-1061 Chateau Kirwan 已建立，金額 NT$69,000" },
  { id: "n62", type: "cs", title: "CS-1062 ORD-1061 等待主管確認" },
  { id: "n63", type: "shipment", title: "ORD-1063 Chateau Cantemerle 已付款，金額 NT$58,000" },
  { id: "n64", type: "shipment", title: "ORD-1064 Chateau Pape Clement 已出貨，金額 NT$142,000" },
  { id: "n65", type: "cs", title: "CS-1065 ORD-1064 客戶要求修改地址" },
  { id: "n66", type: "shipment", title: "ORD-1066 Chateau La Mission Haut Brion 已建立，金額 NT$258,000" },
  { id: "n67", type: "shipment", title: "ORD-1067 Chateau La Conseillante 已付款，金額 NT$186,000" },
  { id: "n68", type: "cs", title: "CS-1068 ORD-1067 等待物流配送" },
  { id: "n69", type: "shipment", title: "ORD-1069 Chateau L'Evangile 已完成付款，金額 NT$208,000" },
  { id: "n70", type: "shipment", title: "ORD-1070 Chateau Troplong Mondot 已建立，金額 NT$132,000" },
  { id: "n71", type: "cs", title: "CS-1071 ORD-1070 客戶已簽收" },
  { id: "n72", type: "shipment", title: "ORD-1072 Chateau Bellevue Mondotte 已付款，金額 NT$198,000" },
  { id: "n73", type: "shipment", title: "ORD-1073 Chateau Clinet 已配送完成，金額 NT$146,000" },
  { id: "n74", type: "cs", title: "CS-1074 ORD-1073 等待結案" },
  { id: "n75", type: "shipment", title: "ORD-1075 Chateau Hosanna 已建立，金額 NT$168,000" },
  { id: "n76", type: "shipment", title: "ORD-1076 Chateau La Fleur Petrus 已付款，金額 NT$236,000" },
  { id: "n77", type: "cs", title: "CS-1077 ORD-1076 客戶要求補件" },
  { id: "n78", type: "shipment", title: "ORD-1078 Krug Grande Cuvee 已建立，金額 NT$88,000" },
  { id: "n79", type: "shipment", title: "ORD-1079 Cristal 已付款，金額 NT$148,000" },
  { id: "n80", type: "cs", title: "CS-1080 ORD-1079 等待倉庫出貨" },
  { id: "n81", type: "shipment", title: "ORD-1081 Salon Blanc de Blancs 已配送完成，金額 NT$398,000" },
  { id: "n82", type: "shipment", title: "ORD-1082 Dom Ruinart 已建立，金額 NT$108,000" },
  { id: "n83", type: "cs", title: "CS-1083 ORD-1082 客戶要求修改收件人" },
  { id: "n84", type: "shipment", title: "ORD-1084 Bollinger RD 已付款，金額 NT$132,000" },
  { id: "n85", type: "shipment", title: "ORD-1085 Louis Roederer Cristal 已完成配送，金額 NT$165,000" },
  { id: "n86", type: "cs", title: "CS-1086 ORD-1085 等待客戶評價" },
  { id: "n87", type: "shipment", title: "ORD-1087 Domaine Dujac 已建立，金額 NT$196,000" },
  { id: "n88", type: "shipment", title: "ORD-1088 Clos de Tart 已付款，金額 NT$335,000" },
  { id: "n89", type: "cs", title: "CS-1089 ORD-1088 客戶要求延後配送" },
  { id: "n90", type: "shipment", title: "ORD-1090 Echezeaux Grand Cru 已配送完成，金額 NT$238,000" },
  { id: "n91", type: "shipment", title: "ORD-1091 Le Pin 已建立，金額 NT$720,000" },
  { id: "n92", type: "cs", title: "CS-1092 ORD-1091 等待主管審核" },
  { id: "n93", type: "shipment", title: "ORD-1093 Harlan Estate 已付款，金額 NT$388,000" },
  { id: "n94", type: "shipment", title: "ORD-1094 Screaming Eagle 已出貨，金額 NT$1,180,000" },
  { id: "n95", type: "cs", title: "CS-1095 ORD-1094 客戶要求加購商品" },
  { id: "n96", type: "shipment", title: "ORD-1096 Romanee Conti 已建立，金額 NT$1,280,000" },
  { id: "n97", type: "shipment", title: "ORD-1097 Perro de Grado 已付款，金額 NT$102,000" },
  { id: "n98", type: "cs", title: "CS-1098 ORD-1097 等待報關" },
  { id: "n99", type: "shipment", title: "ORD-1099 DK Le Latour 已配送完成，金額 NT$186,000" },
  { id: "n100", type: "shipment", title: "ORD-1100 五糧液 已完成配送，金額 NT$156,000" },
];

function getNotificationIcon(type: string): string {
  switch (type) {
    case "shipment": return "ri-truck-line";
    case "account": return "ri-bill-line";
    case "cs": return "ri-customer-service-2-line";
    default: return "ri-notification-3-line";
  }
}

function generateNotifications(): { id: string; type: string; icon: string; title: string; time: string; isNew: boolean }[] {
  // 每次頁面載入真正隨機洗牌
  const shuffled = [...ALL_NOTIFICATIONS_RAW];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const selected = shuffled.slice(0, 7);

  // 分配由近到遠的時間
  const timeLabels: string[] = [];
  for (let idx = 0; idx < selected.length; idx++) {
    if (idx === 0) {
      timeLabels.push(`${Math.floor(Math.random() * 55) + 5} 秒前`);
    } else if (idx === 1) {
      timeLabels.push(`${Math.floor(Math.random() * 9) + 1} 分前`);
    } else if (idx === 2) {
      timeLabels.push(`${Math.floor(Math.random() * 20) + 10} 分前`);
    } else if (idx === 3) {
      timeLabels.push(`${Math.floor(Math.random() * 2) + 1} 小時前`);
    } else if (idx === 4) {
      const h = Math.floor(Math.random() * 5) + 3;
      const d = new Date();
      d.setHours(d.getHours() - h);
      timeLabels.push(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
    } else if (idx === 5) {
      const h = Math.floor(Math.random() * 6) + 8;
      const d = new Date();
      d.setHours(d.getHours() - h);
      timeLabels.push(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
    } else {
      timeLabels.push("昨天");
    }
  }

  return selected.map((n, idx) => ({
    id: n.id,
    type: n.type,
    icon: getNotificationIcon(n.type),
    title: n.title,
    time: timeLabels[idx],
    isNew: idx < 3,
  }));
}

export default function HomePage() {
  const navigate = useNavigate();
  const { userRole, userName, employeeId } = useUser();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayRecords, setTodayRecords] = useState<ClockInRecord[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logText, setLogText] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  const [dbOrderCount, setDbOrderCount] = useState(0);
  const [recentShippingOrders, setRecentShippingOrders] = useState<
    { id: string; orderNumber: string; customer: string; product: string; amount: number; status: string }[]
  >([]);
  const [lowStockProducts, setLowStockProducts] = useState<
    { id: string; name: string; brand: string | null; stock: number; safety_stock: number; image: string | null; note: string | null; alert_level: string | null }[]
  >([]);
  const [editingStock, setEditingStock] = useState<{ id: string; field: "stock" | "safety_stock" } | null>(null);
  const [editValue, setEditValue] = useState("");

  const todayRevenue = calcTodayRevenue();
  const monthRevenue = calcMonthRevenue();
  const todayAttendance = calcTodayAttendance();
  const revenueChangePercent = calcRevenueChange();
  const todayOrders = dbOrderCount;
  const revenueTrend = getYearToDateRevenueTrend();
  const notifications = useMemo(() => generateNotifications(), []);

  function getShippingStatusStyle(status: string): string {
    switch (status) {
      case "海運中":
        return "bg-primary-100 text-primary-700";
      case "已到港":
        return "bg-accent-100 text-accent-700";
      case "已轉帳務":
        return "bg-secondary-100 text-secondary-700";
      default:
        return "bg-background-100 text-foreground-600";
    }
  }

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchTodayRecords = useCallback(async () => {
    if (!employeeId) return;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const { data, error: queryError } = await supabase
        .from("clock_in_records")
        .select("*")
        .eq("employee_id", employeeId)
        .gte("clock_time", today.toISOString())
        .lt("clock_time", tomorrow.toISOString())
        .order("clock_time", { ascending: true });

      if (!queryError && data) {
        setTodayRecords(data);
      }
    } catch {
      // ignore
    }
  }, [employeeId]);

  const fetchDashboardData = useCallback(async () => {
    if (!employeeId) return;
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const since = sevenDaysAgo.toISOString();

      const shippingResult = await supabase
        .from("shipping_orders")
        .select("*", { count: "exact", head: true })
        .eq("employee_id", employeeId)
        .gte("created_at", since);

      const shippingCount = shippingResult.error ? 0 : (shippingResult.count ?? 0);
      setDbOrderCount(shippingCount);

      const recentResult = await supabase
        .from("shipping_orders")
        .select("id, order_number, customer, product, amount, status")
        .eq("employee_id", employeeId)
        .neq("status", "已轉帳務")
        .order("created_at", { ascending: false })
        .limit(5);

      if (!recentResult.error && recentResult.data) {
        setRecentShippingOrders(
          recentResult.data.map((row: Record<string, unknown>) => ({
            id: row.id as string,
            orderNumber: (row.order_number as string) || "",
            customer: (row.customer as string) || "",
            product: (row.product as string) || "",
            amount: Number(row.amount) || 0,
            status: (row.status as string) || "",
          }))
        );
      }

      const { data: products, error: productsError } = await supabase
        .from("products")
        .select("id, brand, name, stock, safety_stock, image, description, status")
        .gt("stock", 0)
        .order("stock", { ascending: true })
        .limit(5);

      if (!productsError && products) {
        setLowStockProducts(
          (products as Record<string, unknown>[]).map((row) => ({
            id: row.id as string,
            name: (row.name as string) || "",
            brand: (row.brand as string) || null,
            stock: Number(row.stock) || 0,
            safety_stock: Number(row.safety_stock) || 0,
            image: (row.image as string) || null,
            note: (row.description as string) || null,
            alert_level: (row.status as string) || null,
          }))
        );
      }
    } catch {
      // ignore
    }
  }, [employeeId]);

  useEffect(() => {
    fetchDashboardData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTodayRecords();
    fetchDashboardData();
  }, [fetchTodayRecords, fetchDashboardData]);

  const timeStr = currentTime.toLocaleTimeString("zh-TW", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const dateStr = currentTime.toLocaleDateString("zh-TW", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  });

  const todayClockIn = todayRecords.find((r) => r.type === "clock_in");
  const todayClockOut = todayRecords.find((r) => r.type === "clock_out");
  const isClockedIn = !!todayClockIn && !todayClockOut;
  const isClockedOut = !!todayClockOut;

  let workDurationStr = "00:00";
  if (todayClockIn) {
    const endTime = todayClockOut ? new Date(todayClockOut.clock_time) : currentTime;
    const startTime = new Date(todayClockIn.clock_time);
    workDurationStr = formatDuration(endTime.getTime() - startTime.getTime());
  }

  const handleClock = async (type: "clock_in" | "clock_out") => {
    if (!employeeId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const empPromise = supabase
        .from("employees")
        .select("employee_code, name")
        .eq("id", employeeId)
        .maybeSingle();
      const empTimeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000));
      const empResult = await Promise.race([empPromise, empTimeout]);

      if (!empResult || !empResult.data) {
        setToast("無法取得員工資料，請稍後再試");
        setIsSubmitting(false);
        return;
      }
      const empData = empResult.data;

      const { error } = await supabase.from("clock_in_records").insert({
        employee_id: employeeId,
        employee_name: empData.name,
        employee_code: empData.employee_code,
        type,
        work_log: type === "clock_in" ? logText.trim() || null : null,
        clock_time: new Date().toISOString(),
      });

      if (error) {
        setToast(error.message || "打卡失敗，請稍後再試");
      } else {
        setToast(type === "clock_in" ? "上班打卡成功！" : "下班打卡成功！");
        await fetchTodayRecords();
        if (type === "clock_in") setLogText("");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "打卡失敗，請檢查網路連線";
      setToast(msg);
    }
    setIsSubmitting(false);
    setTimeout(() => setToast(null), 3000);
  };

  const startEdit = (id: string, field: "stock" | "safety_stock", currentValue: number) => {
    setEditingStock({ id, field });
    setEditValue(String(currentValue));
  };

  const saveEdit = async () => {
    if (!editingStock) return;
    const numVal = parseInt(editValue, 10);
    if (isNaN(numVal) || numVal < 0) {
      setToast("請輸入有效的數字");
      return;
    }
    const field = editingStock.field;
    const { error } = await supabase
      .from("products")
      .update({ [field]: numVal })
      .eq("id", editingStock.id);

    if (error) {
      setToast("更新失敗：" + error.message);
    } else {
      setLowStockProducts((prev) =>
        prev.map((p) =>
          p.id === editingStock.id ? { ...p, [field]: numVal } : p
        )
      );
      setToast("庫存已更新");
    }
    setEditingStock(null);
    setEditValue("");
    setTimeout(() => setToast(null), 3000);
  };

  const cancelEdit = () => {
    setEditingStock(null);
    setEditValue("");
  };

  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-foreground-800 text-background-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <i className="ri-check-line text-primary-400" />
          {toast}
        </div>
      )}

      <div className="space-y-1 mb-2">
        <h1 className="text-xl font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
          貨物商業管理後台
        </h1>
        <p className="text-sm text-foreground-500">
          貨物商業管理後台 — 內部人員專用管理系統，提供商品上架、製程參數調整、庫存盤點與電商訂單出貨管理。
        </p>
      </div>

      <div>
        <h2 className="text-base font-bold text-foreground-900 mb-1" style={{ fontFamily: "var(--font-heading)" }}>
          今日營運概覽
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4">
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">單日營收</span>
            <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-money-cny-circle-line text-primary-500 text-xs" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900">{formatCurrency(todayRevenue)}</p>
          <div className="flex items-center gap-1 mt-1.5">
            <i className={`${revenueChangePercent < 0 ? "ri-arrow-down-line" : "ri-arrow-up-line"} text-primary-500 text-xs`} />
            <span className="text-xs font-medium text-primary-600">{revenueChangePercent > 0 ? "+" : ""}{revenueChangePercent}%</span>
            <span className="text-xs text-foreground-400">較昨日</span>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">本月總額</span>
            <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-bar-chart-grouped-line text-primary-500 text-xs" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900">{formatCurrency(monthRevenue)}</p>
          <div className="flex items-center gap-1 mt-1.5">
            <span className="text-xs text-foreground-400">當月累計</span>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">訂單數</span>
            <div className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-secondary-500 text-xs" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900">{todayOrders}</p>
          <div className="flex items-center gap-1 mt-1.5">
            <span className="text-xs text-foreground-400">近七日</span>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">低庫存商品</span>
            <div className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center">
              <i className="ri-arrow-go-back-line text-accent-500 text-xs" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900">{lowStockProducts.length}</p>
          <div className="flex items-center gap-1 mt-1.5">
            <span className="text-xs text-foreground-400">項警示</span>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">今日出勤率</span>
            <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-user-follow-line text-primary-500 text-xs" />
            </div>
          </div>
          <p className="text-lg font-bold text-foreground-900">{todayAttendance.rate}</p>
          <div className="flex items-center gap-1 mt-1.5">
            <span className="text-xs text-foreground-400">
              出勤 {todayAttendance.present} / 應到 {todayAttendance.total}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-primary-500" />
              <span className="text-sm font-semibold text-foreground-900">員工打卡</span>
            </div>
            <span className="text-xs text-foreground-400">{userName}</span>
          </div>
          <div className="p-4 space-y-4">
            <div className="text-center py-2">
              <p className="text-xs text-foreground-400 mb-1">{dateStr}</p>
              <p className="text-3xl font-bold text-foreground-900 tracking-wider" style={{ fontFamily: "var(--font-body)" }}>
                {timeStr}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleClock("clock_in")}
                disabled={isClockedIn || isClockedOut || isSubmitting}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  isClockedIn || isClockedOut || isSubmitting
                    ? "bg-background-100 text-foreground-400 cursor-not-allowed"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer"
                }`}
              >
                {isSubmitting && !isClockedIn ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-login-circle-line" />}
                上班打卡
              </button>
              <button
                onClick={() => handleClock("clock_out")}
                disabled={!isClockedIn || isSubmitting}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  !isClockedIn || isSubmitting
                    ? "bg-background-100 text-foreground-400 cursor-not-allowed"
                    : "bg-foreground-800 text-background-50 hover:bg-foreground-700 cursor-pointer"
                }`}
              >
                {isSubmitting && isClockedIn ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-logout-circle-r-line" />}
                下班打卡
              </button>
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-foreground-500">
                今日狀態:
                <span className={`ml-1 font-medium ${isClockedOut ? "text-foreground-400" : isClockedIn ? "text-primary-600" : "text-accent-600"}`}>
                  {isClockedOut ? "已下班" : isClockedIn ? "工作中" : "未打卡"}
                </span>
              </span>
              <span className="text-foreground-400">工作時長 {workDurationStr}</span>
            </div>

            {todayRecords.length > 0 && (
              <div className="bg-background-100 rounded-lg p-3 space-y-2">
                {todayClockIn && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-500 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                      上班時間
                    </span>
                    <span className="text-foreground-700 font-medium">{formatTime(todayClockIn.clock_time)}</span>
                  </div>
                )}
                {todayClockOut && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-500 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-foreground-400" />
                      下班時間
                    </span>
                    <span className="text-foreground-700 font-medium">{formatTime(todayClockOut.clock_time)}</span>
                  </div>
                )}
                {todayClockIn?.work_log && (
                  <div className="text-xs text-foreground-600 pt-1 border-t border-background-200">
                    <span className="text-foreground-400">工作內容：</span>{todayClockIn.work_log}
                  </div>
                )}
              </div>
            )}

            {!isClockedIn && !isClockedOut && (
              <div className="space-y-2">
                <label className="text-xs text-foreground-500 block">工作日誌</label>
                <textarea
                  value={logText}
                  onChange={(e) => setLogText(e.target.value)}
                  placeholder="請輸入今日工作內容..."
                  maxLength={500}
                  className="w-full h-24 px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground-400">{logText.length}/500</span>
                </div>
              </div>
            )}

            <button
              onClick={() => navigate("/clock-in-record")}
              className="w-full text-center text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer py-2 rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center gap-1"
            >
              查看全部打卡紀錄 <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-line-chart-line text-primary-500" />
              <span className="text-sm font-semibold text-foreground-900">營收趨勢</span>
              <span className="text-xs text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded font-medium">{revenueChangePercent > 0 ? "+" : ""}{revenueChangePercent}%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-400">
                {revenueTrend.length > 0 ? `${revenueTrend[0].date} ~ ${revenueTrend[revenueTrend.length - 1].date}` : ""}
              </span>
            </div>
          </div>
          <div className="p-4">
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-200))" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "oklch(var(--foreground-400))" }}
                    dy={8}
                    interval={getXTickInterval()}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    ticks={[1000000, 2000000, 3000000, 4000000]}
                    tick={{ fontSize: 11, fill: "oklch(var(--foreground-400))" }}
                    tickFormatter={(v: number) => `${(v / 10000).toFixed(0)}萬`}
                    dx={-4}
                  />
                  <Tooltip
                    formatter={(value: number) => [formatCurrency(value), "營收"]}
                    labelFormatter={(label: string) => `日期: ${label}`}
                    contentStyle={{
                      backgroundColor: "oklch(var(--background-50))",
                      border: "1px solid oklch(var(--background-200))",
                      borderRadius: "8px",
                      fontSize: "12px",
                      boxShadow: "0 2px 8px oklch(var(--background-300) / 0.3)",
                    }}
                  />
                  <Line
                    type="basis"
                    dataKey="revenue"
                    stroke="oklch(var(--primary-500))"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{
                      r: 5,
                      fill: "oklch(var(--primary-500))",
                      stroke: "oklch(var(--background-50))",
                      strokeWidth: 2,
                    }}
                    isAnimationActive={false}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-notification-3-line text-primary-500" />
              <span className="text-sm font-semibold text-foreground-900">最新通知</span>
            </div>
            <button className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer">查看全部</button>
          </div>
          <div className="divide-y divide-background-100">
            {notifications.map((note) => (
              <button
                key={note.id}
                onClick={() => navigate("/shipping")}
                className="w-full flex items-start gap-3 p-3 hover:bg-background-50/80 text-left cursor-pointer transition-colors"
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                  note.type === "cs"
                    ? "bg-accent-100"
                    : note.type === "account"
                    ? "bg-secondary-100"
                    : "bg-primary-100"
                }`}>
                  <i className={`${note.icon} ${
                    note.type === "cs"
                      ? "text-accent-500"
                      : note.type === "account"
                      ? "text-secondary-500"
                      : "text-primary-500"
                  } text-xs`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground-700 leading-relaxed line-clamp-2">{note.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-foreground-400">{note.time}</span>
                    {note.isNew && (
                      <span className="text-[10px] font-medium text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">NEW</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-file-list-3-line text-primary-500" />
              <span className="text-sm font-semibold text-foreground-900">最新訂單</span>
            </div>
            <button className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer">查看全部</button>
          </div>
          <div className="divide-y divide-background-100">
            {recentShippingOrders.length > 0 ? (
              recentShippingOrders.map((order) => (
                <div key={order.id} className="flex items-center justify-between p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">{order.product}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{order.customer}</p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="text-sm font-semibold text-foreground-900">{formatCurrency(order.amount)}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium mt-0.5 inline-block ${getShippingStatusStyle(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-xs text-foreground-400">
                <i className="ri-inbox-line mr-1" />尚無貨運訂單
              </div>
            )}
          </div>
          <div className="px-3 py-2 border-t border-background-100">
            <button onClick={() => navigate("/shipping")} className="w-full text-center text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer py-1">
              管理訂單
            </button>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-alarm-warning-line text-accent-500" />
              <span className="text-sm font-semibold text-foreground-900">庫存商品</span>
            </div>
            <div className="flex items-center gap-2">
              {userRole === "admin" && (
                <button onClick={() => navigate("/products")} className="text-xs bg-primary-500 text-background-50 hover:bg-primary-600 px-2.5 py-1 rounded-md font-medium cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-add-line mr-0.5" />管理商品
                </button>
              )}
            </div>
          </div>
          <div className="divide-y divide-background-100">
            {lowStockProducts.length > 0 ? (
              lowStockProducts.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-3"
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-background-100">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-box-3-line text-foreground-400 text-sm" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground-900 truncate">{item.name}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {item.brand && <span className="text-[10px] text-foreground-500">{item.brand}</span>}
                      {/* Editable stock */}
                      {editingStock?.id === item.id && editingStock?.field === "stock" ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-[10px] text-foreground-400">庫存:</span>
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                            onBlur={cancelEdit}
                            className="w-12 px-1 py-0.5 text-[10px] border border-primary-400 rounded focus:outline-none focus:ring-1 focus:ring-primary-400 text-foreground-900"
                            autoFocus
                          />
                        </span>
                      ) : (
                        <button
                          onClick={() => startEdit(item.id, "stock", item.stock)}
                          className="text-[10px] text-foreground-500 hover:text-primary-600 cursor-pointer transition-colors flex items-center gap-1 group"
                          title="點擊編輯庫存"
                        >
                          庫存: <span className="font-medium text-foreground-700 group-hover:text-primary-600">{item.stock}</span>
                          <i className="ri-pencil-line text-[9px] opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      )}
                      {/* Editable safety stock */}
                      {editingStock?.id === item.id && editingStock?.field === "safety_stock" ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-[10px] text-foreground-400">安全:</span>
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                            onBlur={cancelEdit}
                            className="w-12 px-1 py-0.5 text-[10px] border border-primary-400 rounded focus:outline-none focus:ring-1 focus:ring-primary-400 text-foreground-900"
                          />
                        </span>
                      ) : (
                        <button
                          onClick={() => startEdit(item.id, "safety_stock", item.safety_stock)}
                          className="text-[10px] text-foreground-500 hover:text-primary-600 cursor-pointer transition-colors flex items-center gap-1 group"
                          title="點擊編輯安全庫存"
                        >
                          安全: <span className="font-medium text-foreground-700 group-hover:text-primary-600">{item.safety_stock}</span>
                          <i className="ri-pencil-line text-[9px] opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      )}
                    </div>
                    {item.note && (
                      <p className="text-[10px] text-foreground-400 mt-1 line-clamp-1">{item.note}</p>
                    )}
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                    item.stock <= 0 ? "bg-accent-100 text-accent-700" :
                    item.stock <= item.safety_stock ? "bg-secondary-100 text-secondary-700" :
                    "bg-primary-100 text-primary-700"
                  }`}>
                    {item.stock <= 0 ? "缺貨" : item.stock <= item.safety_stock ? "偏低" : "注意"}
                  </span>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-xs text-foreground-400">
                <i className="ri-check-line text-primary-500 mr-1" />庫存充足，無警示
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}